import { george, helpers, observer, pr, React, RouteComponentProps, theme, ui } from "aaa";
import { ChooseEmailDropdown } from "components/common/ChooseEmail";
import { GdsFakeActionButton } from "components/common/GdsFakeActionButton";
import { GdsFakeActionButtonContainer } from "components/common/GdsFakeActionButtonContainer";
import { themeColors } from "muiTheme";
import { useEffect } from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { TeamcardLinkageRoutes } from "./TeamcardLinkageRoutes";
import { TeamcardLinkageStore } from "./TeamcardLinkageStore";
import { GenericAPIClient } from "network/APIClient";
import { GenericDocument } from "types/documents";
import { b64ToBlob, b64ToUint8 } from "util/helpers";
import * as FileSaver from "file-saver";
import "./CustomButton.css";
import { IconFile } from "components/util/primitives";

type IProps = RouteComponentProps<{}> & {
    store: TeamcardLinkageStore
};



const DownloadDocument = ({ onDownload, title, style }: { onDownload: () => void, title: string; style?: React.CSSProperties }) => (
    <div
        role="button"
        onClick={onDownload}
        className="theme-secondary-action-label-color"
        style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            padding: 12,
            border: `1px solid ${themeColors.grey_200}`,
            backgroundColor: themeColors.grey_100,
            cursor: "pointer",
            ...style,
        }}
    >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IconFile />
            <div style={{ fontWeight: 500, color: "black", whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "70%" }}>{title}</div>
        </div>
        <pr.IconChevronRight style={{ color: "#A7B6C7", flexShrink: 0 }} />
    </div>
)


export const TeamcardLinkageEmail = observer((props: IProps) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const onSubmit = async () => {
        try {
            await props.store.acceptInvitation();
            pushRoute(TeamcardLinkageRoutes.SIGNING);
        } catch (error) {
            pushRoute("/error");
        }
    };

    const handleDownload = async (url: string, title: string, type: "contract" | "terms" | "dataProtection" | "fernFing") => {
        try {
            const response = await GenericAPIClient.requestType<GenericDocument>({
                // For local testing we replace the https:// prefix with our proxy url
                url: url.replace("https://", ""),
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/x-www-form-urlencoded"
                },
                parse: json => json as GenericDocument
            });

            if (atob(response.content).slice(0, 1025).includes("%PDF-")) {
                if (helpers.isAndroid() || helpers.isIos()) {
                    props.store.pdfData = b64ToUint8(response.content);
                    pushRoute(TeamcardLinkageRoutes.PDF);
                } else {
                    const blob = b64ToBlob(response.content, "application/pdf");
                    FileSaver.saveAs(blob, title);
                }
            }

            if (type === "contract") {
                props.store.contractDownloaded = true;
            } else if (type === "terms") {
                props.store.termsDownloaded = true;
            } else if (type === "dataProtection") {
                props.store.dataProtectionDownloaded = true;
            } else if (type === "fernFing") {
                props.store.fernFingDownloaded = true;
            }
        } catch (error) {
            pushRoute("/error");
        }
    };

    if (props.store.isLoading) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    return (
        <george.Card
            elevated={false}
            style={{ borderRadius: 8 }}
        >
            <div style={{ padding: "16px 16px" }}>
                <pr.P_22_TITLE style={{ fontWeight: 700, lineHeight: "28px" }}>
                    {ui.__("teamcardLinkage.emailPage.headline")}
                </pr.P_22_TITLE>

                <pr.P_16_CONTENT style={{ marginTop: theme.MARGIN }}>
                    {ui.__("teamcardLinkage.emailPage.headline.subtext")}
                </pr.P_16_CONTENT>

                <div style={{ marginTop: theme.MARGIN }}>
                    <ChooseEmailDropdown
                        contactsStore={props.store.contactsStore}
                    />
                </div>

                <pr.P_16_CONTENT style={{ marginTop: theme.MARGIN }}>
                    {ui.__("teamcardLinkage.emailPage.documentsInfo")}
                </pr.P_16_CONTENT>

                {props.store.invitationData?._links.termsDocument &&
                    <DownloadDocument
                        style={{ marginTop: theme.MARGIN, borderRadius: "8x 8x 0 0" }}
                        onDownload={async () => {
                            if (props.store.invitationData?._links.termsDocument.href) {
                                await handleDownload(
                                    props.store.invitationData?._links.termsDocument.href,
                                    props.store.invitationData?._links.termsDocument.title,
                                    "terms"
                                )
                            }
                        }}

                        title={props.store.invitationData?._links.termsDocument.title}
                    />
                }

                {props.store.invitationData?._links.contractDocument &&
                    <DownloadDocument
                        onDownload={async () => {
                            if (props.store.invitationData?._links.contractDocument.href) {
                                await handleDownload(
                                    props.store.invitationData?._links.contractDocument.href,
                                    props.store.invitationData?._links.contractDocument.title,
                                    "contract"
                                )
                            }
                        }}

                        title={props.store.invitationData?._links.contractDocument.title}
                    />
                }

                {props.store.invitationData?._links.dataProtectionDocument &&
                    <DownloadDocument
                        onDownload={async () => {
                            if (props.store.invitationData?._links.dataProtectionDocument.href) {
                                await handleDownload(
                                    props.store.invitationData?._links.dataProtectionDocument.href,
                                    props.store.invitationData?._links.dataProtectionDocument.title,
                                    "dataProtection"
                                )
                            }
                        }}

                        title={props.store.invitationData?._links.dataProtectionDocument.title}
                    />
                }

                {props.store.invitationData?._links.fernFingDocument &&
                    <DownloadDocument
                        style={{ borderRadius: "0 0 8px 8px" }}
                        onDownload={async () => {
                            if (props.store.invitationData?._links.fernFingDocument.href) {
                                await handleDownload(
                                    props.store.invitationData?._links.fernFingDocument.href,
                                    props.store.invitationData?._links.fernFingDocument.title,
                                    "fernFing"
                                )
                            }
                        }}

                        title={props.store.invitationData?._links.fernFingDocument.title}
                    />
                }

                <div style={{ marginTop: theme.MARGIN }}>
                    <george.ApprovalEmbedded
                        defaultChecked={props.store.agreementChecked}
                        hideTitle
                        onCheck={(_, checked) => {
                            props.store.agreementChecked = checked;
                        }}
                        message={ui.__("teamcardLinkage.emailPage.acknowledgement.text")}
                    />
                </div>

            </div>

            <pr.LightSeparatorLine style={{ marginTop: theme.MARGIN_SMALL }} />
            <GdsFakeActionButtonContainer>
                <GdsFakeActionButton
                    className="custom-linkage-gds-button"
                    color="primary"
                    onClick={onSubmit}
                    // fullWidth={externalInterfaceStore.isGeorgeApp}
                    disabled={!props.store.agreementChecked || !props.store.termsDownloaded || !props.store.contractDownloaded || !props.store.dataProtectionDownloaded || !props.store.fernFingDownloaded}
                >
                    {ui.__("teamcardLinkage.emailPage.button.sign")}
                </GdsFakeActionButton>
                <GdsFakeActionButton
                    onClick={() => {
                        pushRoute(TeamcardLinkageRoutes.ROOT);
                    }}
                // fullWidth={externalInterfaceStore.isGeorgeApp}
                >
                    {ui.__("common.back")}
                </GdsFakeActionButton>
            </GdsFakeActionButtonContainer>
        </george.Card >
    );
});



// WEBPACK FOOTER //
// ./src/components/teamcardLinkage/TeamcardLinkageEmail.tsx